import { RestLink } from 'apollo-link-rest';
import invariant from 'tiny-invariant';

import { config } from 'src/config';
import type { UpgradeToProMutationVariables } from 'src/shared/graphql/generated';

type ValidFormData = Record<string, string | boolean | number>;

const isValidFormData = (data: object): data is ValidFormData =>
  Object.values(data).every(
    v =>
      typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean',
  );

const serializeAsFormUrl = (data: ValidFormData, headers: Headers) => {
  const body = Object.entries(data).reduce(
    (encoded, [key, value]) =>
      `${encoded ? `${encoded}&` : ''}${encodeURIComponent(
        key,
      )}=${encodeURIComponent(value)}`,
    '',
  );

  headers.set('Content-Type', 'application/x-www-form-urlencoded');

  return { body, headers };
};

const createRestLink = () =>
  new RestLink({
    endpoints: {
      partners: config.PARTNERS_API_URI,
      apiV2: config.API_URI_V2,
    },
    uri: config.API_URI,
    bodySerializers: {
      'form-url': (data: unknown, headers) => {
        invariant(
          data && typeof data === 'object' && isValidFormData(data),
          'RestLink: invalid data for `form` body serializer',
        );

        return serializeAsFormUrl(data, headers);
      },
      'upgrade-to-pro': (
        { company, mobilePhone, zipCode }: UpgradeToProMutationVariables,
        headers,
      ) => {
        const data = {
          'user[office_phone]': mobilePhone,
          'wallet[billing_address_postal_code]': zipCode,
          'org[name]': company,
          'org[parent_app_id]': 'generic_pro',
          trial: true,
        };

        return serializeAsFormUrl(data, headers);
      },
    },
  });

export { createRestLink };
